import React, { useEffect, useState, useRef } from 'react';
import { Grid, Typography, Box, Button, IconButton } from '@mui/material';
import MainCard from '../../ui-component/cards/MainCard';
import { gridSpacing } from '../../store/constant';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import { useSelector } from "react-redux";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

function ManageExcelUpload() {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [replaceFileId, setReplaceFileId] = useState(null);

    const org_id = useSelector((state) => state?.account?.org?.id || state?.account?.org_data?.data?.id);
    const user_email = useSelector((state) => state?.account?.user?.email);

    // Fetch uploaded files
    const fetchUploadedFiles = async () => {
        try {
            const response = await axiosInstance.get(`${configData.API_SERVER}/activity/get_uploaded_files/?org_id=${org_id}`);
            setUploadedFiles(response.data.files);
        } catch (error) {
            console.error("Error fetching uploaded files:", error);
        }
    };

    useEffect(() => {
        fetchUploadedFiles();
    }, []);

    // Handle file selection for replacement
    const handleFileChange = (event, fileId) => {
        const file = event.target.files[0];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['csv', 'xlsx', 'xls'];
        if (allowedExtensions.includes(fileExtension)) {
            setSelectedFile(file);
            setReplaceFileId(fileId);
        } else {
            alert('Invalid file format. Please select a CSV, XLSX, or XLS file.');
        }
    };

    // Handle file replacement
    const handleReplaceFile = async () => {
        if (selectedFile && replaceFileId) {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('file_id', replaceFileId);
            formData.append('org_id', org_id);
            formData.append('user_email', user_email);

            try {
                await axiosInstance.post(`${configData.API_SERVER}/activity/replace_file/`, formData);
                alert('File replaced successfully.');
                setSelectedFile(null);
                setReplaceFileId(null);
                fetchUploadedFiles();
            } catch (error) {
                console.error("Error replacing file:", error);
                alert('Error replacing file.');
            }
        }
    };

    // Handle file deletion
    const handleDeleteFile = async (fileId) => {
        if (window.confirm("Are you sure you want to delete this file?")) {
            try {
                await axiosInstance.post(`${configData.API_SERVER}/activity/delete_file/`, { file_id: fileId, org_id });
                alert('File deleted successfully.');
                fetchUploadedFiles();
            } catch (error) {
                console.error("Error deleting file:", error);
                alert('Error deleting file.');
            }
        }
    };

    return (
        <MainCard title="Manage Excel Upload">
            <Grid container spacing={gridSpacing}>
                {uploadedFiles.length > 0 ? (
                    uploadedFiles.map((file) => (
                        <Grid item lg={4} sm={6} key={file.id}>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: 2,
                                    p: 2,
                                    textAlign: 'center',
                                    position: 'relative'
                                }}
                            >
                                <Typography variant="h6">{file.name}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Uploaded on: {new Date(file.uploaded_at).toLocaleDateString()}
                                </Typography>

                                {/* Replace File Button */}
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    Replace File
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(event) => handleFileChange(event, file.id)}
                                    />
                                </Button>

                                {/* Delete Button */}
                                <IconButton
                                    onClick={() => handleDeleteFile(file.id)}
                                    color="error"
                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))
                ) : (
                    <Typography variant="body1" sx={{ p: 2 }}>
                        No files uploaded yet.
                    </Typography>
                )}
            </Grid>

            {/* Replace File Upload Action */}
            {selectedFile && replaceFileId && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body1">
                        Selected File: {selectedFile.name}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleReplaceFile} sx={{ mt: 1 }}>
                        Confirm Replace
                    </Button>
                </Box>
            )}
        </MainCard>
    );
}

export default ManageExcelUpload;
